import {
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import type { ReactNode } from "react";
import { IconChecklist, IconClock, IconTarget } from "@tabler/icons-react";
import {
  Job,
  getStatusColor,
  getStatusText,
} from "@labeling-tools/ui-components/src/utils/helpers";

/**
 * A reviewer's benchmark quiz results.
 *
 * Takes values, not a quiz id, and fetches nothing — the same card renders one
 * quiz or an aggregate of several depending on what the caller passes. Callers
 * supply their own title/subtitle for that reason.
 *
 * Every metric is nullable because lebron returns nulls rather than dividing by
 * zero: a quiz that was never answered has no precision and no pace.
 */
export interface QuizMetricCardProps {
  /** e.g. "Quiz complete" for one quiz, "All quizzes" for an aggregate. */
  title: string;
  /** e.g. "Benchmark Store · 8/6/2026, 2:14:03 PM". */
  subtitle?: string;
  /** Renders the badge; omit for aggregates, which have no single status. */
  status?: Job["status"];
  /** 0..1, formatted here as a percentage. */
  precision: number | null;
  correct: number;
  answered: number;
  avgSecondsPerTask: number | null;
  tasksPerHour: number | null;
  /** Omit to hide the button — a modal supplies its own close control. */
  onBack?: () => void;
  backLabel?: string;
}

interface QuizMetricTileProps {
  icon: ReactNode;
  color: string;
  label: string;
  value: string | null;
  caption: string;
}

/** One metric. Mirrors ProfilePage's MetricCard so the two read as one system. */
function QuizMetricTile({
  icon,
  color,
  label,
  value,
  caption,
}: QuizMetricTileProps) {
  return (
    <div>
      <Group gap="xs" align="center" wrap="nowrap" mb={6}>
        <ThemeIcon variant="light" color={color} size={28} radius="md">
          {icon}
        </ThemeIcon>
        <Text size="sm" c="dimmed" fw={500}>
          {label}
        </Text>
      </Group>
      {/* An em dash, never "NaN%" — a quiz with nothing scorable still renders. */}
      <Text fz={36} fw={700} lh={1} c={value === null ? "dimmed" : undefined}>
        {value ?? "—"}
      </Text>
      <Text size="xs" c="dimmed" mt={8}>
        {caption}
      </Text>
    </div>
  );
}

export function QuizMetricCard({
  title,
  subtitle,
  status,
  precision,
  correct,
  answered,
  avgSecondsPerTask,
  tasksPerHour,
  onBack,
  backLabel = "Back to my quizzes",
}: QuizMetricCardProps) {
  return (
    <Card withBorder radius="lg" padding="lg" className="shadow-sm">
      <Stack gap="lg">
        <Stack gap={4}>
          <Group gap="sm" align="center" wrap="nowrap">
            <Text fz={20} fw={700} lh={1.2}>
              {title}
            </Text>
            {status && (
              <Badge
                color={getStatusColor(status)}
                variant="light"
                size="sm"
                radius="sm"
              >
                {getStatusText(status)}
              </Badge>
            )}
          </Group>
          {subtitle && (
            <Text size="sm" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xl">
          <QuizMetricTile
            icon={<IconTarget size={18} />}
            color="blue"
            label="Precision"
            value={
              precision === null ? null : `${Math.round(precision * 100)}%`
            }
            caption={`${correct} of ${answered} correct`}
          />
          <QuizMetricTile
            icon={<IconClock size={18} />}
            color="indigo"
            label="Avg. time per task"
            value={
              avgSecondsPerTask === null
                ? null
                : `${Math.round(avgSecondsPerTask)}s`
            }
            caption={`Across ${answered} ${answered === 1 ? "task" : "tasks"}`}
          />
          <QuizMetricTile
            icon={<IconChecklist size={18} />}
            color="teal"
            label="Tasks / hr"
            value={tasksPerHour === null ? null : tasksPerHour.toFixed(1)}
            caption="Your pace this quiz"
          />
        </SimpleGrid>

        {onBack && (
          <Group>
            <Button variant="default" onClick={onBack}>
              {backLabel}
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
